// src/store/api/followApi.js
import { baseApi } from "./baseApi";

export const followApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Follow user
    followUser: builder.mutation({
      query: (targetUserId) => ({
        url: `/Post/follow/${targetUserId}`,
        method: "POST",
      }),
      invalidatesTags: ["Follow", "Profile", "UserProfile"],
    }),

    // Unfollow user
    unfollowUser: builder.mutation({
      query: (targetUserId) => ({
        url: `/Post/unfollow/${targetUserId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Follow", "Profile", "UserProfile"],
    }),
    // Remove follower
    removeFollower: builder.mutation({
      query: (followerUserId) => ({
        url: `/Post/remove-follower/${followerUserId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Follow", "Profile", "UserProfile"],
    }),

    // Get followers/following count - can specify userId to get another user's count
    getFollowCount: builder.query({
      query: (userId) => {
        const params = {};
        if (userId) {
          params.userId = userId;
        }
        return {
          url: "/Post/count",
          params,
        };
      },
      transformResponse: (response) => ({
        followers: response?.followers || 0,
        following: response?.following || 0,
        isFollowing: response?.isFollowing || false,
      }),
      providesTags: (result, error, userId) =>
        userId ? [{ type: "Follow", id: userId }] : ["Follow"],
    }),

    // Get followers list - can specify userId to get another user's followers
    getFollowers: builder.query({
      query: (userId) => {
        const params = {};
        if (userId) {
          params.userId = userId;
        }
        return {
          url: "/Post/followers",
          params,
        };
      },
      transformResponse: (response) => {
        const list = response?.$values ?? response ?? [];
        return list.map((user) => ({
          id: user.id,
          name: user.name,
          profilePhotoUrl: user.profilePhotoUrl,
          userType: user.userType,
          bio: user.bio,
          isFollowing: user.isFollowing ?? false,
        }));
      },
      providesTags: (result, error, userId) =>
        userId ? [{ type: "Follow", id: `followers-${userId}` }] : ["Follow"],
    }),

    // Get following list - can specify userId to get another user's following
    getFollowing: builder.query({
      query: (userId) => {
        const params = {};
        if (userId) {
          params.userId = userId;
        }
        return {
          url: "/Post/following",
          params,
        };
      },
      transformResponse: (response) => {
        const list = response?.$values ?? response ?? [];
        return list.map((user) => ({
          id: user.id,
          name: user.name,
          profilePhotoUrl: user.profilePhotoUrl,
          userType: user.userType,
          bio: user.bio,
          isFollowing: user.isFollowing ?? false,
        }));
      },
      providesTags: (result, error, userId) =>
        userId ? [{ type: "Follow", id: `following-${userId}` }] : ["Follow"],
    }),

    // Get users suggested by followers
    getUsersByFollowers: builder.query({
      query: () => ({
        url: "/Post/users-by-followers",
      }),

      transformResponse: (response) => {
        const list = response?.$values ?? response ?? [];
        return list;
      },

      providesTags: ["Follow"],
    }),
  }),
});

export const {
  useFollowUserMutation,
  useUnfollowUserMutation,
  useGetFollowCountQuery,
  useGetFollowersQuery,
  useGetFollowingQuery,
  useRemoveFollowerMutation,
  useGetUsersByFollowersQuery,
} = followApi;
